import * as React from 'react';
import { cn } from '../utils';
import { LineDisplay } from './LineDisplay';
import { ChangePulse, linePulseValue } from './ChangePulse';
import { getLiquidityTooltip, type LiquidityThresholds } from './liquidity';
import { Tooltip } from '../primitives/Tooltip';
import type { EVVariant } from '../utils';
import type { BookLine } from '@sharpr/types';
import { useBook } from './bookCatalog';

export interface BookLinePair {
  over?: BookLine;
  under?: BookLine;
}

export interface SportsbookLineCellProps {
  lines?: BookLinePair;
  className?: string;
  evVariant?: EVVariant;
  liquidityThresholds?: LiquidityThresholds;
  /** Render a blurred ghost in the EV slot when EV is absent (non-subscriber teaser). */
  teaseEv?: boolean;
  /** Render a construction glyph in the EV slot for no-model markets (e.g. MLB batters). */
  teaseConstruction?: boolean;
}

/**
 * A single grid-row slot for one side (over or under). The slot's bounding box
 * IS the hover target for the tooltip. Grid-row guarantees the slot fills the
 * allocated cell area (full column width × half row height) with no flex-item
 * sizing surprises.
 *
 * The outer `<div data-side={side}>` lets click handlers up the tree (the
 * TableRow click delegate) read the side the user actually pointed at via
 * `.closest('[data-side]')` — without it, over/under aren't distinguishable
 * because they share the same `data-book` column wrapper.
 */
const FilledLine: React.FC<{
  line: BookLine;
  evVariant: EVVariant;
  liquidityThresholds?: LiquidityThresholds;
  teaseEv?: boolean;
  teaseConstruction?: boolean;
}> = ({ line, evVariant, liquidityThresholds, teaseEv, teaseConstruction }) => {
  const { name, book } = useBook(line.book);
  const tooltip = getLiquidityTooltip(line, { name, limitKind: book?.limitKind ?? null });
  const content = (
    <LineDisplay
      line={line}
      size="sm"
      evVariant={evVariant}
      liquidityThresholds={liquidityThresholds}
      teaseEv={teaseEv}
      teaseConstruction={teaseConstruction}
    />
  );
  return tooltip ? (
    <Tooltip
      content={tooltip}
      side="top"
      wrapperClassName="block w-full h-full"
      delayMs={120}
    >
      {content}
    </Tooltip>
  ) : (
    content
  );
};

const LineSlot: React.FC<{
  line: BookLine | undefined;
  side: 'over' | 'under';
  evVariant: EVVariant;
  liquidityThresholds?: LiquidityThresholds;
  teaseEv?: boolean;
  teaseConstruction?: boolean;
}> = ({ line, side, evVariant, liquidityThresholds, teaseEv, teaseConstruction }) => {
  // Grid cells fill their track by default. `block w-full h-full` is just belt-
  // and-suspenders; a grid cell with no explicit size gets the track's full
  // width and height. The inner content is free to be centered via its own flex.
  // Over slot gets a bottom divider to match BestLineSummary's visual rhythm.
  const slotClass = cn(
    "relative isolate block w-full h-full",
    side === 'over' && "border-b border-border/30"
  );

  // A one-sided quote leaves the missing side blank; its grid row keeps the height.
  return (
    <div data-side={side} className={slotClass}>
      <ChangePulse value={linePulseValue(line)} />
      {line && (
        <FilledLine line={line} evVariant={evVariant} liquidityThresholds={liquidityThresholds} teaseEv={teaseEv} teaseConstruction={teaseConstruction} />
      )}
    </div>
  );
};

export const SportsbookLineCell: React.FC<SportsbookLineCellProps> = ({
  lines,
  className,
  evVariant = 'default',
  liquidityThresholds,
  teaseEv = false,
  teaseConstruction = false,
}) => {
  if (!lines || (!lines.over && !lines.under)) {
    return (
      <div className={cn(
        "h-full flex items-center justify-center text-xs text-muted-foreground",
        className
      )}>
        —
      </div>
    );
  }

  // Two explicit rows, each fills half the cell. No flex item sizing drama —
  // grid tracks have concrete sizes. `w-full h-full` makes the grid container
  // occupy the full column cell.
  return (
    <div className={cn(
      "h-full w-full grid grid-cols-1 grid-rows-2",
      className
    )}>
      <LineSlot line={lines.over} side="over" evVariant={evVariant} liquidityThresholds={liquidityThresholds} teaseEv={teaseEv} teaseConstruction={teaseConstruction} />
      <LineSlot line={lines.under} side="under" evVariant={evVariant} liquidityThresholds={liquidityThresholds} teaseEv={teaseEv} teaseConstruction={teaseConstruction} />
    </div>
  );
};
